import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Loading, Empty, Th, Td, TableWrap, Money, NativeSelect } from "@/components/kit";
import { dmy, errMsg } from "@/lib/format";

export default function Stock() {
  const { api, t, can } = useApp();
  const [tab, setTab] = useState("state");
  const [items, setItems] = useState(null);
  const [moves, setMoves] = useState(null);
  const [whs, setWhs] = useState([]);
  const [wh, setWh] = useState("");
  const [counts, setCounts] = useState({});
  const load = useCallback(() => {
    api.get("/stock").then((r) => setItems(r.data));
    api.get("/stock/moves").then((r) => setMoves(r.data));
    api.get("/warehouses").then((r) => { setWhs(r.data); setWh((w) => w || r.data[0]?.id || ""); });
  }, [api]);
  useEffect(() => { load(); }, [load]);
  const rw = can("stock", true);
  const whName = (id) => whs.find((w) => w.id === id)?.name || id;
  const addWh = async () => {
    const name = window.prompt(t("warehouse"));
    if (name) { await api.post("/warehouses", { name }); load(); }
  };
  const saveInventory = async () => {
    try {
      const { data } = await api.post("/stock/inventory", { warehouse_id: wh, counts: Object.entries(counts).map(([item_id, qty]) => ({ item_id, qty: +qty })) });
      toast.success(`${data.adjusted} ajustements`);
      setCounts({});
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHeader eyebrow={t("stock")} title={t("stock")}>
        {rw && <Button variant="outline" onClick={addWh} data-testid="stock-add-warehouse"><Plus className="h-4 w-4 me-1" />{t("warehouse")}</Button>}
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border">
          <TabsTrigger value="state" data-testid="stock-tab-state">{t("stock")}</TabsTrigger>
          <TabsTrigger value="moves" data-testid="stock-tab-moves">{t("moves")}</TabsTrigger>
          {rw && <TabsTrigger value="inventory" data-testid="stock-tab-inventory">{t("inventory")}</TabsTrigger>}
        </TabsList>
      </Tabs>
      <Panel>
        {!items ? <Loading /> : tab === "moves" ? (moves.length === 0 ? <Empty /> : (
          <TableWrap>
            <thead className="border-b"><tr><Th>{t("date")}</Th><Th>{t("code")}</Th><Th>{t("description")}</Th><Th>{t("warehouse")}</Th><Th>Type</Th><Th>{t("number")}</Th><Th className="text-end">{t("qty")}</Th></tr></thead>
            <tbody>{moves.map((m) => (
              <tr key={m.id} className="border-b border-slate-50"><Td className="num text-xs">{dmy(m.date)}</Td><Td className="num text-xs">{m.item_code}</Td><Td>{m.item_name}</Td><Td>{whName(m.warehouse_id)}</Td>
                <Td className="text-xs">{m.type === "inventory" ? t("inventory") : t(`type_${m.type}`)}</Td><Td className="num text-xs">{m.document_number}</Td>
                <Td className={`text-end num font-semibold ${m.qty < 0 ? "text-red-600" : "text-emerald-700"}`}>{m.qty > 0 ? "+" : ""}{m.qty}</Td></tr>))}</tbody>
          </TableWrap>
        )) : items.length === 0 ? <Empty text="Activez « Suivi de stock » sur vos articles" /> : (
          <>
            {tab === "inventory" && <div className="mb-4 flex items-center gap-3"><NativeSelect value={wh} onChange={(e) => setWh(e.target.value)} data-testid="inventory-warehouse">{whs.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</NativeSelect>
              <Button onClick={saveInventory} data-testid="inventory-save">{t("save")}</Button></div>}
            <TableWrap>
              <thead className="border-b"><tr><Th>{t("code")}</Th><Th>{t("description")}</Th>{whs.map((w) => <Th key={w.id} className="text-end">{w.name}</Th>)}<Th className="text-end">Total</Th><Th className="text-end">{t("avg_cost")}</Th><Th className="text-end">{t("stock_value")}</Th>{tab === "inventory" && <Th>{t("qty_counted")}</Th>}</tr></thead>
              <tbody>{items.map((i) => (
                <tr key={i.id} className="border-b border-slate-50" data-testid={`stock-row-${i.code}`}>
                  <Td className="num text-xs">{i.code}</Td><Td className="font-medium">{i.alert && <AlertTriangle className="inline h-4 w-4 me-1 text-orange-700" />}{i.name_fr}</Td>
                  {whs.map((w) => <Td key={w.id} className="text-end num">{(i.stock || {})[w.id] || 0}</Td>)}
                  <Td className="text-end num font-semibold">{i.stock_total}</Td><Td className="text-end"><Money v={i.avg_cost} /></Td><Td className="text-end"><Money v={i.value} /></Td>
                  {tab === "inventory" && <Td><Input type="number" className="w-24" value={counts[i.id] ?? ""} onChange={(e) => setCounts({ ...counts, [i.id]: e.target.value })} data-testid={`inventory-count-${i.code}`} /></Td>}
                </tr>))}</tbody>
            </TableWrap>
          </>
        )}
      </Panel>
    </>
  );
}
